// Storage shim — MUST be the first import of the app entry point.
//
// Some browser configurations (Chrome with "block all cookies/site data",
// certain webviews, old Safari private mode) expose window.localStorage as
// null or throw a SecurityError on access. The app reads localStorage in
// dozens of useState initializers, so a broken storage crashed the whole
// app at boot (Sentry: "Cannot read properties of null (reading 'getItem')"
// in ThemeContext). This shim probes both storages and, when broken,
// replaces them with an in-memory fallback so the app degrades gracefully
// (settings just don't persist across reloads).
const makeMemoryStorage = () => {
  let store = {};
  return {
    getItem: (k) => (Object.prototype.hasOwnProperty.call(store, k) ? store[k] : null),
    setItem: (k, v) => { store[String(k)] = String(v); },
    removeItem: (k) => { delete store[k]; },
    clear: () => { store = {}; },
    key: (i) => Object.keys(store)[i] ?? null,
    get length() { return Object.keys(store).length; },
  };
};

const isBroken = (getStorage) => {
  try {
    const s = getStorage();
    if (!s) return true;
    const probe = '__nw_storage_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return false;
  } catch {
    return true;
  }
};

const install = (name) => {
  if (!isBroken(() => window[name])) return;
  try {
    Object.defineProperty(window, name, {
      value: makeMemoryStorage(),
      configurable: true,
    });
  } catch { /* defineProperty blocked — nothing more we can do */ }
};

if (typeof window !== 'undefined') {
  install('localStorage');
  install('sessionStorage');
}
